import * as PIXI from 'pixi.js';
import { rgbaStringToRgbString } from '../../../../libs/videoData';
import type { TextLineMetrics } from '../metrics/TextLineMetrics';
import { TextDirection } from '../../../../enums';
import type { WordElementMetrics } from '../metrics/WordElementMetrics';
import { TextEditingManager } from './TextEditingManager';
import { getRootContainerScale } from './TextEditingUtils';

/**
 * This file contains all methods needed to generate DOM nodes for the Natural Text Editing input
 *
 * !! NOTE !! it is assumed that getBounds() has been called on the parent text asset before calling
 *      any of these methods, i.e. the bounds are up to date.
 */

/**
 * Creates the text editing input with all static property values, static styling,
 * and events listeners attached
 */
export function createTextInput(): HTMLDivElement {
    const textAssetNode = document.createElement('div');
    textAssetNode.contentEditable = 'true';
    textAssetNode.style.color = 'transparent';
    textAssetNode.style.fontFamily = 'inherit';
    textAssetNode.style.width = '100%';
    textAssetNode.style.height = '100%';
    textAssetNode.spellcheck = false;
    textAssetNode.id = TextEditingManager.contentEditableId;
    return textAssetNode;
}

/**
 * Create an HTML node that has non-zero bounds, styled like the text the user
 * will see once that start typing
 *
 * @param caretColor CSS caretColor value
 */
export function createTextPlaceholder(caretColor: string): HTMLDivElement {
    const placeholderNode = document.createElement('div');
    placeholderNode.id = TextEditingManager.placeholderId;
    placeholderNode.style.caretColor = caretColor;
    placeholderNode.style.height = '100%';
    placeholderNode.style.width = '100%';
    return placeholderNode;
}

/**
 * Creates nodes for all text lines and wordElements of a given textAsset
 */
export function buildInputContent(textEditingManager: TextEditingManager): Array<HTMLDivElement> {
    const textAsset = textEditingManager.textAsset;
    const textLineList = textAsset.renderer.metrics?.lines ?? [];
    const textLineNodes = [];

    for (let i = 0; i < textLineList.length; i++) {
        const textLine = textLineList[i];
        const textLineMetrics = textLineList[i];
        if (isEmptyLine(textLineMetrics)) {
            const startIdx = i;
            while (
                i + 1 < textAsset.renderer.metrics.lines.length &&
                isEmptyLine(textAsset.renderer.metrics.lines[i + 1])
            ) {
                i++;
            }
            textLineNodes.push(...createEmptyTextLines(textEditingManager, startIdx, i));
        } else {
            textLineNodes.push(createNonEmptyTextLine(textLine, textEditingManager));
        }
    }

    return textLineNodes;
}

/**
 * Constructs an absolutely positioned block container representing a text line that contains
 * children for each of its wordElements
 */
function createGenericLine(): HTMLDivElement {
    const lineNode = document.createElement('div');
    lineNode.classList.add(TextEditingManager.textLineClass);
    lineNode.style.position = 'absolute';
    lineNode.style.fontFamily = 'inherit';
    return lineNode;
}

/**
 * Creates a container for a text line with at least one glyph. Populates the container
 * with all relevant wordElement nodes.
 */
function createNonEmptyTextLine(
    textLine: TextLineMetrics,
    textEditingManager: TextEditingManager,
): HTMLDivElement {
    const textAsset = textEditingManager.textAsset;
    const lineNode = createGenericLine();
    const textLineMetrics = textLine;

    const textAssetBounds = textAsset.getNTEContainerBounds();
    const wordElementList = textLine.words.flatMap((word) => word.elements);
    const height = textLine.bounds.height / textAsset.referenceToOutputMultiplier;
    const offsetY =
        (textLine.bounds.y - textAsset.renderer.metrics.y) / textAsset.referenceToOutputMultiplier;
    const fontSize = textLineMetrics.style.fontSize / textAsset.referenceToOutputMultiplier;
    // set height styling
    const resize = (canvasWidth: number) => {
        if (canvasWidth === 0) {
            // Canvas is not part of DOM
            return;
        }
        // Update font styles
        const domScale = getRootContainerScale(textAsset.root, canvasWidth);

        // set height styling
        lineNode.style.lineHeight = `${domScale * height}px`;
        lineNode.style.height = `${domScale * height}px`;
        lineNode.style.top = `${domScale * offsetY}px`;
        lineNode.style.fontSize = `${domScale * fontSize}px`;
    };
    resize(textEditingManager.domContainer.clientWidth);
    textEditingManager._addOnResizeCallback(resize);

    const isRTL = textAsset.normalStyle.textDirection === TextDirection.RTL;
    lineNode.style.width = '100%';
    const firstWordEleIdx = textLineMetrics.removedLeadingSpace ? 1 : 0;

    // Create a node for each wordElement
    wordElementList.forEach((wordElement, i) => {
        const caretColor = wordElement.style.fill;
        const isBreakingSpace = PIXI.TextMetrics.isBreakingSpace(wordElement.text);

        const isLeadingWhitespace =
            i === 0 && textLineMetrics.removedLeadingSpace && isBreakingSpace;
        const isTrailingWhitespace =
            i === wordElementList.length - 1 &&
            textLineMetrics.removedTrailingSpace &&
            isBreakingSpace;

        let wordEleNode = null;
        if (isLeadingWhitespace || isTrailingWhitespace) {
            wordEleNode = createResetWordElement(caretColor, wordElement.text);
        } else {
            wordEleNode = createWordElement(textEditingManager, wordElementList, i);
        }

        if (!isRTL && i === 0) {
            const offset =
                (wordElementList[firstWordEleIdx].x - textAsset.renderer.activeTextBounds.x) /
                textAsset.referenceToOutputMultiplier /
                textAssetBounds.width;
            wordEleNode.style.marginLeft = `${offset * 100}%`;
        }

        lineNode.appendChild(wordEleNode);
    });

    return lineNode;
}

/**
 * Creates a series of divs to represent sequential empty lines.
 *
 * Empty lines are always created together since individual metric instances for
 * empty lines may not contain the correct bounds information that will translate
 * nicely to nodes living in the contenteditable.
 *
 * @param startIdx index of the first empty line relative to all text line metrics
 * @param endIdx index of the last empty line relative to all text line metrics
 */
function createEmptyTextLines(
    textEditingManager: TextEditingManager,
    startIdx: number,
    endIdx: number,
): Array<HTMLDivElement> {
    const textAsset = textEditingManager.textAsset;
    const textAssetBounds = textAsset.getBounds(true);
    const lineMetricList = textAsset.renderer.metrics.lines;

    // The height of each line is the cumulative height of the last non-empty line and the next
    // non empty line, divided by the number of empty lines
    const numEmptyLines = endIdx - startIdx + 1;

    let startY = 0;
    if (startIdx > 0) {
        const prevLine = lineMetricList[startIdx - 1];
        const prevLineBounds = prevLine.bounds;
        startY = prevLineBounds.y + prevLineBounds.height;
    }

    const endYIdx = endIdx === lineMetricList.length - 1 ? endIdx : endIdx + 1;
    const endY = lineMetricList[endYIdx].bounds.y;

    const avgHeight = (endY - startY) / numEmptyLines;
    const avgLineHeight = avgHeight / textAsset.normalStyle.fontSize;

    // The horizontal offset, dependent on alignment, is set to match its neighbour
    const nonNewlineLineIdx = startIdx > 0 ? startIdx : endIdx + 1;
    const nonNewlineWordEleList = textAsset.textLinesForEditing[nonNewlineLineIdx].words.flatMap(
        (word) => word.elements,
    );
    const lastWordElement = nonNewlineWordEleList[nonNewlineWordEleList.length - 1];
    const firstWordElement = nonNewlineWordEleList[0];
    const lastWordElementShadowBoundsWidth =
        lastWordElement._shadowBounds.x - lastWordElement._textBounds.x;
    const rightOffset =
        1 -
        (lastWordElement.x - lastWordElementShadowBoundsWidth) /
            textAsset.referenceToOutputMultiplier /
            textAssetBounds.width;
    const firstWordEleShadowBoundsWidth =
        firstWordElement._shadowBounds.x - firstWordElement._textBounds.x;
    const leftOffset = (firstWordElement.x - firstWordEleShadowBoundsWidth) / textAssetBounds.width;

    // Create nodes and apply styling
    const emptyLineNodeList = [];
    let currY = startY;
    for (let i = startIdx; i <= endIdx; i++) {
        const textLineMetrics = lineMetricList[i];
        const emptyLineNode = createGenericLine();
        emptyLineNode.style.lineHeight = `${avgLineHeight * 100}%`;
        emptyLineNode.style.height = `${(avgHeight / textAsset.referenceToOutputMultiplier / textAssetBounds.height) * 100}%`;
        emptyLineNode.style.width = '100%';
        emptyLineNode.style.top = `${(currY / textAsset.referenceToOutputMultiplier / textAssetBounds.height) * 100}%`;
        currY += avgHeight;

        const textAlign = textAsset.textAlign;
        emptyLineNode.style.textAlign = textAlign;

        const wordEleNode = document.createElement('div');
        wordEleNode.appendChild(document.createElement('br'));
        wordEleNode.dataset.text = '\n';
        wordEleNode.classList.add(TextEditingManager.wordElementClass);
        wordEleNode.style.height = '100%';
        wordEleNode.style.caretColor = rgbaStringToRgbString(textLineMetrics.style.fill);

        if (textAlign === 'left') {
            wordEleNode.style.marginLeft = `${leftOffset * 100}%`;
        } else if (textAlign === 'right') {
            wordEleNode.style.marginRight = `${rightOffset * 100}%`;
        }

        emptyLineNode.appendChild(wordEleNode);
        emptyLineNodeList.push(emptyLineNode);
    }

    return emptyLineNodeList;
}

/**
 * Generates a special node for a leading breaking space that had its bounds reset
 *
 * @param caretColor cssString
 */
function createResetWordElement(caretColor: string, dataValue: string): HTMLSpanElement {
    const wordEleNode = createGenericWordElement(caretColor);
    wordEleNode.textContent = '\u200B';
    wordEleNode.dataset.text = dataValue;
    wordEleNode.style.width = '0';
    return wordEleNode;
}

/**
 * Factory method for a node representing a word element
 *
 * @param  caretColor CSS string
 */
function createGenericWordElement(caretColor: string): HTMLSpanElement {
    const wordEleNode = document.createElement('span');
    wordEleNode.classList.add(TextEditingManager.wordElementClass);
    wordEleNode.style.height = '100%';
    wordEleNode.style.display = 'inline-block';
    wordEleNode.style.verticalAlign = 'middle'; // I'm not sure if we want this here, explore more in LU-3468
    wordEleNode.style.whiteSpace = 'pre';
    wordEleNode.style.caretColor = caretColor;
    wordEleNode.style.lineHeight = '100%';
    return wordEleNode;
}

/**
 * Creates an inline HTML node representing a wordElement
 *
 * @param wordElementList a list of WordELement objects corresponding to the line the wordElement belongs in
 * @param idx the index of the wordElement contained in wordElementList to create a node for
 */
function createWordElement(
    textEditingManager: TextEditingManager,
    wordElementList: Array<WordElementMetrics>,
    idx: number,
): HTMLSpanElement {
    const textAsset = textEditingManager.textAsset;
    const currWordEle = wordElementList[idx];
    const wordEleNode = createGenericWordElement(currWordEle.style.fill);
    const metrics = currWordEle;

    // Set the content and any styling based on the content
    if (currWordEle.text === '\n') {
        wordEleNode.dataset.text = '\n';
        wordEleNode.appendChild(document.createElement('br'));
    } else if (currWordEle.text === '\t') {
        wordEleNode.innerText = '\t';
        wordEleNode.style.tabSize = '1';
    } else {
        wordEleNode.innerText = currWordEle.text;
    }

    // Set a direction override if it's different than the root
    const isWhitespace = [' ', '\n', '\t'].some((char) => currWordEle.text === char);
    if (!isWhitespace) {
        const globalTextDirection = textAsset.root.textDirection;
        const localTextDirection = metrics.style.textDirection;
        if (globalTextDirection !== localTextDirection) {
            wordEleNode.setAttribute('dir', localTextDirection);
        }
    }

    wordEleNode.style.fontFamily = metrics.style.fontFamily;

    const resize = (canvasWidth: number) => {
        if (canvasWidth === 0) {
            // Canvas is not part of DOM
            return;
        }

        const metricsWidth = metrics.advanceWidth;

        const domScale = getRootContainerScale(textAsset.root, canvasWidth);

        const fontSize =
            (domScale * metrics.style.fontSize) / textAsset.referenceToOutputMultiplier;
        const height = (domScale * metrics.height) / textAsset.referenceToOutputMultiplier;
        const width = (domScale * metricsWidth) / textAsset.referenceToOutputMultiplier;

        wordEleNode.style.lineHeight = `${height}px`;
        wordEleNode.style.height = `${height}px`;
        wordEleNode.style.fontSize = `${fontSize}px`;
        wordEleNode.style.position = 'relative';
        wordEleNode.style.top = '0px';
        wordEleNode.style.left = '0px';
        wordEleNode.style.width = `${width}px`;
        wordEleNode.style.textAlign = textAsset.textAlign;
    };
    resize(textEditingManager.domContainer.clientWidth);
    textEditingManager._addOnResizeCallback(resize);
    return wordEleNode;
}

/**
 * Determines if the text line does not contain any sprites that have height and width
 */
function isEmptyLine(textLineMetrics: TextLineMetrics): boolean {
    const isJustNewline = textLineMetrics.text === '\n';
    const isJustStrippedWhitespace =
        textLineMetrics.text === ' ' && textLineMetrics.removedLeadingSpace;

    return isJustNewline || isJustStrippedWhitespace;
}
